import { useLocation } from "react-router-dom";

export interface UseIdQueryParamReturn {
  /** The Id value from the URL query parameter, or null if not present */
  idFromUrl: string | null;
  /** The created value from the URL query parameter, or null if not present */
  createdFromUrl: string | null;
  /** Get query parameters object with Id and created preserved if they exist */
  getQueryParamsWithId: () => Record<string, string>;
}

/**
 * Hook to handle the Id and created query parameters in CRM import URLs
 * This allows components to read the Id and created from URL and preserve them during navigation
 * Supports case-insensitive Id parameter matching (Id, id, ID)
 * Supports case-insensitive created parameter matching (created, Created, CREATED)
 * @returns Object containing the Id and created from URL and utility function to get query params
 */
export const useIdQueryParam = (): UseIdQueryParamReturn => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  // Check for Id parameter (case-insensitive)
  let idFromUrl: string | null = null;
  for (const [key, value] of searchParams.entries()) {
    if (key.toLowerCase() === "id") {
      idFromUrl = value;
      break;
    }
  }

  // Check for created parameter (case-insensitive)
  let createdFromUrl: string | null = null;
  for (const [key, value] of searchParams.entries()) {
    if (key.toLowerCase() === "created") {
      createdFromUrl = value;
      break;
    }
  }

  const getQueryParamsWithId = (): Record<string, string> => {
    const params: Record<string, string> = {};

    if (idFromUrl) {
      params.Id = idFromUrl;
    }

    if (createdFromUrl) {
      params.created = createdFromUrl;
    }

    return params;
  };

  return {
    idFromUrl,
    createdFromUrl,
    getQueryParamsWithId,
  };
};
